import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { toTextCase } from 'js-convert-case/lib'
import Avatar from 'components/Avatar'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import { SkeletonLine, SkeletonBlock } from 'components/Skeleton'
import { hideEmail } from 'utils/user'
import { formatDateWithTime } from 'utils/date'
import { logActions } from 'utils/constants'
import { getEstateActivity } from 'services/estateService'
import { getPortfolioActivity } from 'services/portfolioService'

const PAGE_SIZE = 25

const FETCHERS = {
  estate: ({ targetId, page, limit }) => getEstateActivity({ estateId: targetId, page, limit }),
  portfolio: ({ targetId, page, limit }) => getPortfolioActivity({ portfolioId: targetId, page, limit }),
}

const isEmail = value => typeof value === 'string' && value.includes('@')

const isUuidLike = value => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)

const labelForAction = action => {
  const match = logActions.find(entry => entry.key === action)
  if (match) return match.value
  return String(action || 'did something').replace(/[._]/g, ' ')
}

const actorName = user => {
  const name = `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
  return name || 'System'
}

const capitalise = str => (str ? str.charAt(0).toUpperCase() + str.slice(1) : str)

const fieldLabel = key => capitalise(key === 'leadUserId' ? 'Lead' : toTextCase(key))

const valueLabel = value => {
  if (value === null || value === undefined || value === '') return 'not set'
  if (typeof value === 'boolean') return value ? 'yes' : 'no'
  if (isUuidLike(value)) return null
  if (Array.isArray(value)) return value.length ? `${value.length} item${value.length === 1 ? '' : 's'}` : 'none'
  if (typeof value === 'string') return value.replace(/_/g, ' ')
  return String(value)
}

const describeChanges = changes => {
  const parts = Object.entries(changes).map(([key, change]) => {
    const to = valueLabel(change?.to)
    if (to === null) return null
    const from = valueLabel(change?.from)
    return from && from !== to
      ? `${fieldLabel(key)} changed from ${from} to ${to}`
      : `${fieldLabel(key)} set to ${to}`
  }).filter(Boolean)
  return parts.length ? parts.join('; ') : null
}

const renderFileLink = (file, key) => {
  const name = typeof file === 'string' ? file : file?.name
  if (!name) return null
  const url = typeof file === 'object' && file ? file.url : null
  const deleted = typeof file === 'object' && file ? file.deleted : false
  if (url && !deleted) {
    return (
      <FileLink key={key} href={url} target='_blank' rel='noopener noreferrer' onClick={e => e.stopPropagation()}>
        {name}
      </FileLink>
    )
  }
  return <span key={key}>{deleted ? `${name} (deleted)` : name}</span>
}

const renderFileLinks = files => {
  const items = files.map((file, i) => renderFileLink(file, i)).filter(Boolean)
  if (items.length === 0) return null
  return items.flatMap((item, i) => (i === 0 ? [item] : [', ', item]))
}

const renderDetails = entry => {
  const data = entry.data || {}

  if (data.changes && typeof data.changes === 'object' && !Array.isArray(data.changes)) {
    const described = describeChanges(data.changes)
    if (described) return described
  }

  if (Array.isArray(data.files) && data.files.length > 0) {
    const rendered = renderFileLinks(data.files)
    if (rendered) return rendered
  }

  if (data.file && typeof data.file === 'object' && data.file.name) {
    return renderFileLink(data.file, 'file')
  }

  if (data.files && typeof data.files === 'object' && !Array.isArray(data.files)) {
    const names = Object.values(data.files).filter(Boolean)
    if (names.length > 0) return names.join(', ')
  }

  if (data.from !== undefined && data.to !== undefined) {
    const from = valueLabel(data.from)
    const to = valueLabel(data.to)
    if (to !== null) return `Changed from ${from || 'not set'} to ${to}`
  }

  const withRelationType = masked => (typeof data.relationType === 'string' && data.relationType.trim()
    ? `${masked} (as ${toTextCase(data.relationType)})`
    : masked)

  if (isEmail(data.email)) return withRelationType(hideEmail(data.email))
  if (isEmail(data.sharedWith)) return withRelationType(hideEmail(data.sharedWith))
  if (isEmail(data.unsharedWith)) return withRelationType(hideEmail(data.unsharedWith))

  if (typeof data.name === 'string' && data.name.trim()) return data.name
  if (typeof data.fileName === 'string' && data.fileName.trim()) return data.fileName
  if (typeof data.folderName === 'string' && data.folderName.trim()) return data.folderName

  if (typeof data.domain === 'string' && data.domain.trim()) return `Domain: ${data.domain}`

  if (typeof data.relationType === 'string' && data.relationType.trim()) return `As ${toTextCase(data.relationType)}`

  const affected = data.summary && typeof data.summary === 'object' ? data.summary.buildingsAffected : null
  if (typeof affected === 'number') return `${affected} building${affected === 1 ? '' : 's'} affected`

  if (Array.isArray(data.buildingPassportIds) && data.buildingPassportIds.length > 0) {
    return `${data.buildingPassportIds.length} building${data.buildingPassportIds.length === 1 ? '' : 's'}`
  }

  return null
}

const ActivityLog = ({ targetType, targetId }) => {
  const [entries, setEntries] = useState([])
  const [page, setPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(false)
  const requestIdRef = useRef(0)

  const fetchPage = useCallback(pageToLoad => {
    const fetcher = FETCHERS[targetType]
    if (!fetcher || !targetId) return

    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId

    if (pageToLoad <= 1) setLoading(true)
    else setLoadingMore(true)
    setError(false)

    fetcher({ targetId, page: pageToLoad, limit: PAGE_SIZE })
      .then(res => {
        if (requestId !== requestIdRef.current) return
        const incoming = res?.data?.data || []
        const meta = res?.data?.meta || {}
        setEntries(prev => {
          if (pageToLoad <= 1) return incoming
          const existingIds = new Set(prev.map(e => e.id))
          return [...prev, ...incoming.filter(e => !existingIds.has(e.id))]
        })
        setPage(meta.currentPage || pageToLoad)
        setLastPage(meta.lastPage || pageToLoad)
        setTotal(typeof meta.total === 'number' ? meta.total : incoming.length)
      })
      .catch(() => { if (requestId === requestIdRef.current) setError(true) })
      .finally(() => {
        if (requestId !== requestIdRef.current) return
        setLoading(false)
        setLoadingMore(false)
      })
  }, [targetType, targetId])

  useEffect(() => {
    setEntries([])
    setPage(1)
    setLastPage(1)
    setTotal(0)
    fetchPage(1)
  }, [fetchPage])

  if (loading) {
    return (
      <Wrapper aria-busy='true' aria-label='Loading activity'>
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonRow key={i} style={{ animationDelay: `${i * 60}ms` }}>
            <SkeletonBlock $w={36} $h={36} $r='50%' />
            <SkeletonRowBody>
              <SkeletonLine $w='55%' />
              <SkeletonLine $w='28%' $h={10} />
            </SkeletonRowBody>
          </SkeletonRow>
        ))}
      </Wrapper>
    )
  }

  if (error) {
    return <EmptyState>We could not load this activity. Please try again shortly.</EmptyState>
  }

  if (entries.length === 0) {
    return <EmptyState>No activity yet.</EmptyState>
  }

  const hasMore = page < lastPage

  return (
    <Wrapper>
      <Total>Showing {entries.length} of {total} update{total === 1 ? '' : 's'}</Total>
      <List>
        {entries.map(entry => {
          const details = renderDetails(entry)
          return (
            <Row key={entry.id}>
              <Avatar firstName={entry.user?.firstName} lastName={entry.user?.lastName} size={36} />
              <RowBody>
                <RowLine>
                  <ActorName>{actorName(entry.user)}</ActorName> {labelForAction(entry.action)}
                </RowLine>
                {details && <DetailsRow>{details}</DetailsRow>}
                <TimeRow title={formatDateWithTime(entry.createdAt)}>{moment(entry.createdAt).fromNow()}</TimeRow>
              </RowBody>
            </Row>
          )
        })}
      </List>
      <InfiniteScrollSentinel
        hasMore={hasMore}
        loading={loadingMore}
        onLoadMore={() => fetchPage(page + 1)}
      />
    </Wrapper>
  )
}

export default ActivityLog

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`

const Total = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#6b7280'};
`

const List = styled.div`
  display: flex;
  flex-direction: column;
`

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#eceef1'};
  min-width: 0;

  &:last-child {
    border-bottom: none;
  }
`

const RowBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`

const RowLine = styled.div`
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.haiti};
  overflow-wrap: anywhere;
`

const ActorName = styled.span`
  font-weight: 600;
`

const DetailsRow = styled.div`
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#6b7280'};
  overflow-wrap: anywhere;
`

const FileLink = styled.a`
  color: ${({ theme }) => theme.colors.secondary || '#33becc'};
  font-weight: 600;
  text-decoration: none;
  overflow-wrap: anywhere;

  &:hover {
    text-decoration: underline;
  }
`

const TimeRow = styled.div`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#9aa1ac'};
`

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#6b7280'};
`

const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
`

const SkeletonRowBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`
